import { useState } from 'react';
import styled from 'styled-components';
import { ContextSearchDialog } from './ContextSearchDialog';
import type { GlobalSearchSource, SelectedGlobalContext } from '../types';

const Bar = styled.div`
  grid-column: 2;
  grid-row: 2;
  padding: 14px 28px 0;
  background: ${p => p.theme.bg};

  @media (max-width: 920px) {
    grid-column: 1;
    grid-row: 3;
    padding: 14px 16px 0;
  }
`;

const Inner = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 20px;
  background: linear-gradient(135deg, ${p => p.theme.surface} 0%, ${p => p.theme.surfaceSoft} 100%);
  box-shadow: ${p => p.theme.shadow};

  @media (max-width: 980px) {
    flex-direction: column;
  }
`;

const Summary = styled.div`
  flex: 0 0 180px;
  min-width: 0;
  padding: 4px 6px;

  @media (max-width: 980px) {
    flex-basis: auto;
  }
`;

const Eyebrow = styled.div`
  color: ${p => p.theme.primary};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .1em;
  text-transform: uppercase;
`;

const ScopeTitle = styled.div`
  margin-top: 7px;
  color: ${p => p.theme.text};
  font-family: 'Raleway', system-ui, sans-serif;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.015em;
`;

const ScopeMeta = styled.div`
  margin-top: 4px;
  color: ${p => p.theme.muted};
  font-size: 12px;
  line-height: 1.35;
`;

const Cards = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ContextCard = styled.div`
  position: relative;
  min-width: 0;
  padding: 14px 38px 14px 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  background: ${p => p.theme.surface};
  box-shadow: ${p => p.theme.shadow};
`;

const Kind = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: ${p => p.theme.primary};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${p => p.theme.primary};
    box-shadow: 0 0 0 4px ${p => p.theme.primarySoft};
  }
`;

const CardTitle = styled.div`
  margin-top: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.text};
  font-size: 14px;
  font-weight: 900;
`;

const CardDetail = styled.div`
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 12px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border: 1px solid ${p => p.theme.border};
  border-radius: 999px;
  background: ${p => p.theme.surfaceStrong};
  color: ${p => p.theme.muted};
  font-size: 13px;
  font-weight: 900;
  line-height: 1;

  &:hover {
    background: ${p => p.theme.dangerSoft};
    color: ${p => p.theme.dangerStrong};
  }
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: 980px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  }
`;

const AddActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

const InlineButton = styled.button`
  flex: 0 0 auto;
  border: 1px solid ${p => p.theme.border};
  border-radius: 999px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.primary};
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 900;

  &:hover {
    background: ${p => p.theme.primarySoft};
    border-color: ${p => p.theme.primary};
  }
`;

const ClearButton = styled.button`
  flex: 0 0 auto;
  border: 1px solid ${p => p.theme.border};
  border-radius: 12px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.text};
  padding: 9px 11px;
  font-size: 13px;
  font-weight: 900;

  &:hover {
    background: ${p => p.theme.dangerSoft};
    color: ${p => p.theme.dangerStrong};
  }
`;

type Props = {
  context: SelectedGlobalContext | null;
  onSelectContext: (context: SelectedGlobalContext) => void;
  onClear: () => void;
};

type ContextCardData = {
  key: 'company' | 'workspace' | 'user';
  kind: string;
  title: string;
  detail: string;
  onRemove: () => void;
};

function sourceLabel(source: GlobalSearchSource) {
  if (source === 'metabase') return 'Workspace';
  return source === 'pipedrive' ? 'Pipedrive' : 'PostHog';
}

function compact(parts: Array<string | number | null | undefined>) {
  return parts.filter(part => part !== null && part !== undefined && `${part}`.trim()).join(' · ');
}

function scopeName(context: SelectedGlobalContext) {
  const hasCompany = Boolean(context.company);
  const hasWorkspace = Boolean(context.workspace);
  const hasUser = Boolean(context.user);
  if (hasCompany && hasWorkspace && hasUser) return 'Full context';
  if (hasUser && hasWorkspace) return 'User + workspace';
  if (hasUser && hasCompany) return 'User + company';
  if (hasCompany && hasWorkspace) return 'Company + workspace';
  if (hasUser) return 'User only';
  if (hasWorkspace) return 'Workspace only';
  if (hasCompany) return 'Company only';
  return 'Context selected';
}

function userTitle(context: SelectedGlobalContext) {
  return context.user?.name || context.user?.email || context.user?.emails?.[0] || 'User';
}

export function SelectedContextBar({ context, onSelectContext, onClear }: Props) {
  const [dialog, setDialog] = useState<'company' | 'workspace' | 'user' | null>(null);
  if (!context) return null;
  const activeContext = context;
  const isUserFirst = activeContext.selectedType === 'user' && Boolean(activeContext.user);

  function clearCompany() {
    if (!activeContext.user) {
      onClear();
      return;
    }
    onSelectContext({
      selectedType: 'user',
      selectedAt: Date.now(),
      company: null,
      workspace: null,
      user: activeContext.user,
    });
  }

  function clearWorkspace() {
    if (!isUserFirst && !activeContext.company) {
      onClear();
      return;
    }
    onSelectContext({
      ...activeContext,
      selectedType: isUserFirst ? 'user' : 'company',
      selectedAt: Date.now(),
      workspace: null,
      user: isUserFirst ? activeContext.user : null,
    });
  }

  function clearUser() {
    if (!activeContext.company && !activeContext.workspace) {
      onClear();
      return;
    }
    onSelectContext({
      ...activeContext,
      selectedType: activeContext.workspace ? 'workspace' : activeContext.company ? 'company' : activeContext.selectedType,
      selectedAt: Date.now(),
      user: null,
    });
  }

  const userSources = activeContext.user?.sources?.map(sourceLabel).join(' + ');
  const cards: ContextCardData[] = [];

  if (isUserFirst && activeContext.user) {
    cards.push({
      key: 'user',
      kind: 'User',
      title: userTitle(activeContext),
      detail: compact([activeContext.user.email || activeContext.user.emails?.[0], userSources]),
      onRemove: clearUser,
    });
  }

  if (activeContext.company) {
    cards.push({
      key: 'company',
      kind: 'Company',
      title: activeContext.company.name,
      detail: compact([`ID ${activeContext.company.id}`, activeContext.company.clientKey && `Client ${activeContext.company.clientKey}`]),
      onRemove: clearCompany,
    });
  }

  if (activeContext.workspace) {
    cards.push({
      key: 'workspace',
      kind: 'Workspace',
      title: activeContext.workspace.name,
      detail: compact([`ID ${activeContext.workspace.id}`, activeContext.workspace.token && `Token ${activeContext.workspace.token}`]),
      onRemove: clearWorkspace,
    });
  }

  if (!isUserFirst && activeContext.user) {
    cards.push({
      key: 'user',
      kind: 'User',
      title: userTitle(activeContext),
      detail: compact([activeContext.user.email || activeContext.user.emails?.[0], userSources]),
      onRemove: clearUser,
    });
  }

  const addActions = [
    isUserFirst && activeContext.user && !activeContext.company ? { key: 'company', label: 'Add company', onClick: () => setDialog('company' as const) } : null,
    activeContext.company && !activeContext.workspace ? { key: 'workspace', label: 'Add workspace', onClick: () => setDialog('workspace' as const) } : null,
    !isUserFirst && activeContext.workspace && !activeContext.user ? { key: 'user', label: 'Add user', onClick: () => setDialog('user' as const) } : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClick: () => void }>;

  return (
    <Bar>
      <Inner>
        <Summary>
          <Eyebrow>Selected context</Eyebrow>
          <ScopeTitle>{scopeName(activeContext)}</ScopeTitle>
          <ScopeMeta>{cards.length} of 3 cards selected</ScopeMeta>
        </Summary>
        <Cards>
          {cards.slice(0, 3).map(card => (
            <ContextCard key={card.key}>
              <Kind>{card.kind}</Kind>
              <CardTitle title={card.title}>{card.title}</CardTitle>
              {card.detail && <CardDetail title={card.detail}>{card.detail}</CardDetail>}
              <RemoveButton type="button" aria-label={`Clear ${card.kind}`} onClick={card.onRemove}>x</RemoveButton>
            </ContextCard>
          ))}
        </Cards>
        <Actions>
          {addActions.length > 0 && (
            <AddActions>
              {addActions.map(action => <InlineButton key={action.key} type="button" onClick={action.onClick}>{action.label}</InlineButton>)}
            </AddActions>
          )}
          <ClearButton type="button" onClick={onClear}>Clear all</ClearButton>
        </Actions>
        {dialog && (
          <ContextSearchDialog
            mode={dialog}
            context={activeContext}
            onSelect={onSelectContext}
            onClose={() => setDialog(null)}
          />
        )}
      </Inner>
    </Bar>
  );
}
